export type ObjectStatus = 'new' | 'partner_review' | 'match' | 'contacted' | 'rejected';

export interface CommuteInfo {
  minutes: number | null;
  distanceKm: number | null;
  apiVersion: string | null;
}

export interface BreakdownEntry {
  key: string;
  score: number;
  contribution: number;
  value: number | string | null;
  phase?: 'rule' | 'llm';
  weight?: number;
  /** false = keine Info (unbekanntes LLM-Feld) → floss nicht in den Score ein. */
  applicable?: boolean;
}

export interface ObjectEvent {
  eventType: string;
  occurredAt: number;
  source: string;
  payload: Record<string, unknown> | null;
}

export interface CommuteByTarget {
  target: string;
  displayName: string;
  address: string;
  role: 'primary' | 'secondary';
  /** Karten-Emoji (💼 Arbeit, 🏠 Zuhause, 👵 Eltern). */
  icon?: string;
  /** Anker-Koordinaten (für die Karte). */
  lat: number;
  lng: number;
  driving: CommuteInfo | null;
  transit: CommuteInfo | null;
  bestMinutes: number | null;
}

export interface ObjectView {
  id: number;
  assignedProfile: string | null;
  dealType: 'miete' | 'kauf' | null;
  objektTyp: 'wohnung' | 'haus' | 'grundstueck' | null;
  address: string | null;
  lat: number | null;
  lng: number | null;
  geocodePrecision: string | null;
  status: ObjectStatus;
  statusChangedAt: number | null;
  createdAt: number;
  /** Phase-3-Schwelle des Such-Profils (Swipe-Gate; steuert das Phasen-Badge). */
  p3Threshold: number;
  /** Alter des Inserats in Tagen (ab erster Mail). */
  ageDays: number;
  /** true = älter als die Frische-Grenze (fresh_days) → „älteres Inserat". */
  stale: boolean;
  listing: {
    id: number;
    url: string;
    title: string | null;
    portal: string;
    imageUrl: string | null;
    firstSeenAt: number;
    lastSeenAt: number;
  } | null;
  latestSnapshot: {
    price: number | null;
    areaSqm: number | null;
    rooms: number | null;
    locationText: string | null;
    capturedAt: number;
  } | null;
  commutes: CommuteByTarget[];
  bestCommuteMin: number | null;
  avgCommuteMin: number | null;
  pendelTarget: { displayName: string; address: string } | null;
  extracted: Record<string, string | null>;
  /** true = die eingeloggte Person hat dieses Objekt gemerkt (veto-immune Merkliste). */
  favorited: boolean;
  /** Private Notiz aus der Merkliste (nur diese Person). */
  favoriteNote: string | null;
  /** Freie Notiz (zuletzt gespeicherter Text). */
  note?: string | null;
  /** Verlauf: Status-Wechsel + Ablehngründe (ohne note-Events), neueste zuerst. */
  events?: ObjectEvent[];
  score: {
    /** Raw Score wie er beim Scoring berechnet wurde. */
    total: number;
    /** Score nach Time-Decay (0.05/Tag ab Listing firstSeenAt). */
    effectiveTotal: number;
    /** Wieviele Tage Decay angewendet wurden (float). */
    decayDays: number;
    isReif: boolean;
    scoredAt: number;
    killedBy: string | null;
    breakdown: BreakdownEntry[] | null;
  } | null;
}

export interface HealthResponse {
  status: string;
  uptimeSec: number;
  timestamp: string;
}

export interface IngestionHealth {
  rawEmailCount: number;
  perPortal: Array<{ portal: string; count: number }>;
  lastMail: { receivedAt: string; portal: string; subject: string } | null;
}

/** Fehler mit HTTP-Status, damit Aufrufer 401 (nicht angemeldet) von 5xx/Netz unterscheiden. */
export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, init);
  if (!r.ok) {
    const text = await r.text();
    throw new ApiError(r.status, `${r.status} ${r.statusText}: ${text}`);
  }
  return (await r.json()) as T;
}

export interface ListObjectsOpts {
  profile?: string;
  /** Filter auf ein benutzerdefiniertes Such-Profil. */
  searchProfileId?: number;
  status?: ObjectStatus;
  limit?: number;
  /** Filter auf effective Score. 0 = kein Filter. (Legacy, flache Schwelle.) */
  minScore?: number;
  /** Nur reife Objekte: Score ≥ der pro-Profil Swipe-Schwelle jedes Objekts. */
  ripe?: boolean;
}

export interface ListObjectsResponse {
  objects: ObjectView[];
  totalCount: number;
  totalCountUnfiltered?: number;
}

export async function listObjects(opts: ListObjectsOpts = {}): Promise<ListObjectsResponse> {
  const params = new URLSearchParams();
  if (opts.profile) params.set('profile', opts.profile);
  if (opts.searchProfileId) params.set('searchProfileId', String(opts.searchProfileId));
  if (opts.status) params.set('status', opts.status);
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.minScore !== undefined) params.set('minScore', String(opts.minScore));
  if (opts.ripe) params.set('ripe', 'true');
  return fetchJson(`/api/objects?${params.toString()}`);
}

export async function getObject(id: number): Promise<ObjectView> {
  return fetchJson(`/api/objects/${id}`);
}

export async function updateStatus(
  id: number,
  status: ObjectStatus,
): Promise<{ ok: boolean; status: ObjectStatus }> {
  return fetchJson(`/api/objects/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
}

export async function setNote(id: number, text: string): Promise<{ ok: boolean }> {
  return fetchJson(`/api/objects/${id}/note`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
}

/** Merkliste-Toggle für die eingeloggte Person. favorited:true → merken, false → entfernen. */
export async function setFavorite(
  id: number,
  favorited: boolean,
  note?: string,
): Promise<{ ok: boolean; favorited: boolean }> {
  return fetchJson(`/api/objects/${id}/favorite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ favorited, ...(note !== undefined ? { note } : {}) }),
  });
}

export interface FavoritesResponse {
  objects: ObjectView[];
  /** false = kein Personen-Login (Impersonation/alte Session) → Merken nicht möglich. */
  personalLogin: boolean;
}

/** Die veto-immune Merkliste der eingeloggten Person (nach Score, ohne Status-Filter). */
export async function getFavorites(): Promise<FavoritesResponse> {
  return fetchJson('/api/favorites');
}

export async function getHealth(): Promise<HealthResponse> {
  return fetchJson('/api/health');
}

export async function getIngestionHealth(): Promise<IngestionHealth> {
  return fetchJson('/api/health/ingestion');
}

// ─── Auth ───────────────────────────────────────────────────────

export interface AuthUser {
  id: number;
  email: string;
  name: string | null;
  isOwner: boolean;
  /** true = echte Google-Session · false = Übergangsmodus (Default-User ohne Login). */
  viaSession: boolean;
}

/** Echte Login-Identität (Owner) — Basis für Owner-only-UI, auch während Impersonation. */
export interface RealUser {
  id: number;
  email: string;
  name: string | null;
  isOwner: boolean;
}

export interface MeResponse {
  /** Effektiver User (bei Impersonation das angesehene Konto). */
  user: AuthUser;
  realUser: RealUser | null;
  /** true, wenn der Owner gerade ein fremdes Konto ansieht. */
  impersonating: boolean;
  /** Login-Mail der Person (unterscheidet Personen im geteilten Konto). null = kein Personen-Login → Merken aus. */
  loginEmail: string | null;
  /** Bei Impersonation: als welche Person angesehen wird (nur Lese-Sicht). null = ganzes Konto. */
  viewAsLogin: string | null;
  googleConfigured: boolean;
}

export async function getMe(): Promise<MeResponse> {
  return fetchJson('/api/auth/me');
}

/** Owner: Konto wechseln (Impersonation). userId = eigene id → zurück zum eigenen Konto. */
export async function switchAccount(
  userId: number,
  loginEmail?: string,
): Promise<{ ok: boolean; actingAs: number; viewAs?: string | null }> {
  return fetchJson(`/api/auth/switch/${userId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(loginEmail ? { loginEmail } : {}),
  });
}

/** Owner: verwaltetes Konto anlegen (ohne Google-Login). */
export async function createUser(
  name: string,
  email?: string,
): Promise<{ ok: boolean; id: number; email: string }> {
  return fetchJson('/api/admin/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email: email || undefined }),
  });
}

export async function logout(): Promise<{ ok: boolean }> {
  return fetchJson('/api/auth/logout', { method: 'POST' });
}

// ─── Konto (self-service) + User-Verwaltung (owner) ─────────────

export interface AdminUser {
  id: number;
  email: string;
  name: string | null;
  status: 'active' | 'invited' | 'suspended';
  createdAt: number;
  lastLoginAt: number | null;
  objectCount: number;
  isOwner: boolean;
  /** Google-Login-Mails des Kontos (≥2 = geteiltes Konto → „Ansehen als Person"). */
  logins: string[];
}

export async function getUsers(): Promise<{ users: AdminUser[] }> {
  return fetchJson('/api/admin/users');
}

export interface AdminUsage {
  id: number;
  email: string;
  name: string | null;
  objects: number;
  swiped: number;
  hot: number;
  contacted: number;
  rejected: number;
  positiveRate: number | null;
  rejectReasons: Array<{ category: string; count: number }>;
  logins: Array<{
    login: string;
    positiv: number;
    abgelehnt: number;
    sonstige: number;
    lastActivityAt: number | null;
  }>;
  devices: Array<{
    loginEmail: string | null;
    platform: string;
    standalone: boolean;
    permission: 'granted' | 'denied' | 'default' | 'unsupported';
    subscribed: boolean;
    supported: boolean;
    ios: boolean;
    lastSeenAt: number | null;
  }>;
  /** Swipe-Aktivität pro Tag (letzte 30 Tage, rückwirkend aus Events). */
  activityByDay: Array<{ day: string; swipes: number }>;
  /** Besuche pro Login (ab Aktivierung des Loggings). */
  visits: Array<{ login: string | null; lastVisitAt: number | null; visits30d: number }>;
  lastVisitAt: number | null;
  lastActivityAt: number | null;
}
export async function getAdminUsage(): Promise<{ usage: AdminUsage[] }> {
  return fetchJson('/api/admin/usage');
}

// ── Betrieb / Diagnose ───────────────────────────────────────────────────────

export interface AdminNotification {
  id: number;
  userId: number;
  userName: string | null;
  userEmail: string | null;
  objectId: number | null;
  channel: string;
  ok: boolean;
  error: string | null;
  delivered: number;
  pushType: string;
  title: string;
  score: number | null;
  createdAt: number;
}
export interface AdminNotificationsResponse {
  summary: { attempts7d: number; delivered7d: number; failed7d: number };
  notifications: AdminNotification[];
}
export async function getAdminNotifications(
  onlyErrors = false,
  limit = 100,
): Promise<AdminNotificationsResponse> {
  const p = new URLSearchParams();
  if (onlyErrors) p.set('onlyErrors', 'true');
  p.set('limit', String(limit));
  return fetchJson(`/api/admin/notifications?${p.toString()}`);
}

export interface AdminDecision {
  id: number;
  occurredAt: number;
  userId: number | null;
  userName: string | null;
  login: string | null;
  source: string; // 'user' (Phase 3) | 'partner' (Phase 4)
  eventType: string; // 'rejected' | 'partner_review' | 'partner_approved'
  category: string | null;
  note: string | null;
  objectId: number;
  title: string | null;
  url: string | null;
  portal: string | null;
  ort: string | null;
  objektTyp: string | null;
}
export async function getAdminDecisions(opts?: {
  userId?: number;
  type?: string;
  limit?: number;
}): Promise<{ decisions: AdminDecision[] }> {
  const p = new URLSearchParams();
  if (opts?.userId) p.set('userId', String(opts.userId));
  if (opts?.type) p.set('type', opts.type);
  if (opts?.limit) p.set('limit', String(opts.limit));
  return fetchJson(`/api/admin/decisions?${p.toString()}`);
}

export interface AdminFailure {
  id: number;
  stage: string;
  userId: number | null;
  userName: string | null;
  userEmail: string | null;
  objectId: number | null;
  provider: string | null;
  message: string;
  detail: string | null;
  createdAt: number;
  resolvedAt: number | null;
}
export interface AdminMailboxError {
  userId: number;
  userName: string | null;
  userEmail: string | null;
  error: string | null;
  at: number | null;
}
export interface AdminFailuresResponse {
  openByStage: Record<string, number>;
  failures: AdminFailure[];
  mailboxErrors: AdminMailboxError[];
}
export async function getAdminFailures(
  opts: { stage?: string; includeResolved?: boolean; limit?: number } = {},
): Promise<AdminFailuresResponse> {
  const p = new URLSearchParams();
  if (opts.stage) p.set('stage', opts.stage);
  if (opts.includeResolved) p.set('includeResolved', 'true');
  p.set('limit', String(opts.limit ?? 100));
  return fetchJson(`/api/admin/failures?${p.toString()}`);
}
export async function resolveFailure(id: number): Promise<{ ok: boolean }> {
  return fetchJson(`/api/admin/failures/${id}/resolve`, { method: 'POST' });
}

/** Anzeige-Labels für die Fehler-Stufen. */
export const FAILURE_STAGE_LABELS: Record<string, string> = {
  llm_extraction: 'LLM-Analyse',
  scrape: 'Inserat-Abruf',
  notification: 'Benachrichtigung',
  geocode: 'Geocoding',
  routing: 'Routing',
  parse: 'Mail-Parser',
  mail_poll: 'Postfach-Abruf',
  pipeline_crash: 'Pipeline-Absturz',
};

export interface DeviceStateReport {
  deviceId: string;
  standalone: boolean;
  permission: 'granted' | 'denied' | 'default' | 'unsupported';
  subscribed: boolean;
  supported: boolean;
  ios: boolean;
  /** Client-Hinweis fürs Geräte-Label (z. B. 'iPad', das im UA als Mac erscheint). */
  platformHint?: string;
}
/** Meldet den Geräte-/Push-Zustand an den Server (Installations-Fortschritt-Tracking). */
export async function postDeviceState(payload: DeviceStateReport): Promise<void> {
  await fetchJson('/api/device/state', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/** Anzeige-Labels für alle je verwendeten Ablehngründe (inkl. der alten 12 aus der Historie). */
export const REJECT_LABELS: Record<string, string> = {
  lage: 'Lage & Umgebung',
  umgebung: 'Umgebung',
  preis: 'Preis',
  groesse: 'Größe',
  zimmer: 'Zimmer-Anzahl',
  zustand: 'Zustand / Ausstattung',
  aussenbereich: 'Außenbereich',
  pendel: 'Pendel',
  vibe: 'Vermieter/Vibe',
  duplikat: 'Duplikat',
  inseratweg: 'Inserat weg',
  sonstiges: 'Sonstiges',
  unbekannt: 'ohne Grund',
};

export async function setUserStatus(
  id: number,
  status: 'active' | 'invited' | 'suspended',
): Promise<{ ok: boolean }> {
  return fetchJson(`/api/admin/users/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
}

/** Konto-(Benachrichtigungs-)E-Mail bzw. Name ändern. */
export async function updateUser(
  id: number,
  patch: { email?: string; name?: string },
): Promise<{ ok: boolean }> {
  return fetchJson(`/api/admin/users/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

export interface AccountLogin {
  id: number;
  email: string;
  linked: boolean;
}
export async function getUserLogins(id: number): Promise<{ logins: AccountLogin[] }> {
  return fetchJson(`/api/admin/users/${id}/logins`);
}
export async function addUserLogin(id: number, email: string): Promise<{ ok: boolean }> {
  return fetchJson(`/api/admin/users/${id}/logins`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}
export async function removeUserLogin(id: number, loginId: number): Promise<{ ok: boolean }> {
  return fetchJson(`/api/admin/users/${id}/logins/${loginId}`, { method: 'DELETE' });
}

export async function exportAccount(): Promise<unknown> {
  return fetchJson('/api/account/export');
}

export async function deleteAccount(): Promise<{ ok: boolean }> {
  return fetchJson('/api/account', { method: 'DELETE' });
}

export interface StatsResponse {
  totalObjects: number;
  funnel: { new: number; partner_review: number; match: number; contacted: number; rejected: number };
  swipeReady: number;
  byProfileNew: Record<string, number>;
  byDealTypeNew: Record<string, number>;
  scoreBuckets: {
    ge75: number;
    ge65: number;
    ge50: number;
    lt50: number;
    killed: number;
    unscored: number;
  };
  todayNew: number;
  last7dNew: number;
  /** 7-Tage-Funnel auf echtem Maileingang: entered → alive → phase1 → phase2 → phase3. */
  funnel7: { entered: number; alive: number; phase1: number; phase2: number; phase3: number };
  perPortal: Array<{ portal: string; count: number }>;
}

export async function getStats(): Promise<StatsResponse> {
  return fetchJson('/api/stats');
}

// ─── LLM Status (Rate-Limits + Log) ───────────────────────────────

export interface LLMLimitState {
  provider: 'groq' | 'gemini';
  model: string;
  limitRequestsPerMin: number | null;
  remainingRequestsPerMin: number | null;
  resetRequestsMs: number | null;
  limitTokensPerMin: number | null;
  remainingTokensPerMin: number | null;
  resetTokensMs: number | null;
  lastUpdatedAt: number;
  lastRateLimitedAt: number | null;
  lastCallStatus: 'ok' | 'rate-limited' | 'error' | 'parse-error' | null;
}

export interface LLMLogEntry {
  id: number;
  timestamp: number;
  provider: 'groq' | 'gemini';
  model: string;
  objectId: number | null;
  status: 'ok' | 'rate-limited' | 'error' | 'parse-error';
  httpStatus: number | null;
  promptTokens: number | null;
  completionTokens: number | null;
  durationMs: number;
  error: string | null;
}

export async function getLLMLimits(): Promise<{ models: LLMLimitState[] }> {
  return fetchJson('/api/llm/limits');
}

export async function getLLMLog(limit = 50): Promise<{ entries: LLMLogEntry[] }> {
  return fetchJson(`/api/llm/log?limit=${limit}`);
}

// ─── Funnel (Schwellen-Tuning) ───────────────────────────────

export interface FunnelObject {
  id: number;
  phase1Score: number | null;
  phase2Score: number | null;
  isReif: boolean;
  killed: boolean;
  verdict: 'hot' | 'pass' | 'pending';
}

export interface FunnelProfileData {
  total: number;
  objects: FunnelObject[];
}

export interface FunnelResponse {
  profiles: Record<string, FunnelProfileData>;
}

export async function getFunnel(): Promise<FunnelResponse> {
  return fetchJson('/api/funnel');
}

// ─── Settings + Anker ───────────────────────────────────────────

export interface UserSettings {
  userId: number;
  wohnungActive: boolean;
  bueroActive: boolean;
  p1Threshold: number;
  p2Threshold: number;
  freshDays: number;
  ntfyTopic: string | null;
  emailNotify: boolean;
  webpushNotify: boolean;
  updatedAt: number;
}

export interface UserAnchor {
  id: number;
  userId: number;
  targetKey: string;
  displayName: string;
  address: string;
  lat: number | null;
  lng: number | null;
  icon: string;
  role: 'primary' | 'secondary';
  scoringWeightFactor: number;
  maxCommuteMin: number | null;
  profileScope: 'A_wohnung' | 'B_buero' | 'both';
  active: boolean;
  createdAt: number;
}

export interface AnchorInput {
  displayName: string;
  address: string;
  icon: string;
  role: 'primary' | 'secondary';
  scoringWeightFactor: number;
  maxCommuteMin: number | null;
  profileScope: 'A_wohnung' | 'B_buero' | 'both';
  active: boolean;
}

export interface GeocodePreview {
  lat: number;
  lng: number;
  precision: string;
  formattedAddress: string;
}

export async function getSettings(): Promise<{ settings: UserSettings; anchors: UserAnchor[] }> {
  return fetchJson('/api/settings');
}

export async function updateSettings(
  patch: Partial<
    Pick<
      UserSettings,
      'wohnungActive' | 'bueroActive' | 'p1Threshold' | 'p2Threshold' | 'freshDays' | 'ntfyTopic' | 'emailNotify' | 'webpushNotify'
    >
  >,
): Promise<{ ok: boolean; settings: UserSettings }> {
  return fetchJson('/api/settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

// ─── Suchkriterien (Miete + Kauf) ───────────────────────────────
export interface WohnungCriteria {
  p1Threshold: number;
  p3Threshold: number;
  preis: { importance: number; comfortable: number; budget: number; max: number; killFactor: number };
  kaufpreis: { importance: number; comfortable: number; budget: number; max: number; killFactor: number };
  pricePerSqm: { importance: number; ideal: number; max: number };
  baujahr: { importance: number; minYear: number };
  wohnflaeche: { importance: number; ideal: number; good: number; min: number };
  zimmer: { importance: number; ideal: number; min: number };
  pendelArbeit: { importance: number; ideal: number; zero: number; kill: number };
  pendelEltern: { importance: number; ideal: number; zero: number };
  oevNaehe: { importance: number; ideal: number; maxWalkMin: number };
  aussenbereich: { importance: number; required: boolean; minSqm: number };
  befristung: { importance: number };
  energieklasse: { importance: number };
  abloese: { importance: number };
  killZustandSanierung: boolean;
  killStockwerk3OhneLift: boolean;
  killEgOhneGarten: boolean;
}

export interface LocationRules {
  importance: number;
  excludedDistricts: number[];
  districtPriority: Record<string, number>;
  municipalityPriority: Record<string, number>;
}

export interface SearchProfile {
  id: number;
  name: string;
  dealType: 'miete' | 'kauf';
  objektTyp: 'wohnung' | 'haus' | 'grundstueck' | 'egal';
  region: 'wien' | 'umland' | 'beide';
  active: boolean;
  sortOrder: number;
  config: WohnungCriteria;
  location: LocationRules;
  /** Basisgewicht je Config-Kriterium (für die effektive-Gewicht-Anzeige). */
  baseWeights: Record<string, number>;
}

export async function getSearchProfiles(): Promise<{ profiles: SearchProfile[] }> {
  return fetchJson('/api/search-profiles');
}

export async function createSearchProfile(body: {
  name: string;
  dealType: 'miete' | 'kauf';
  objektTyp: 'wohnung' | 'haus' | 'grundstueck' | 'egal';
  region: 'wien' | 'umland' | 'beide';
}): Promise<{ profile: SearchProfile; rescored: number }> {
  return fetchJson('/api/search-profiles', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateSearchProfile(
  id: number,
  patch: Partial<{
    name: string;
    dealType: 'miete' | 'kauf';
    objektTyp: 'wohnung' | 'haus' | 'grundstueck' | 'egal';
    region: 'wien' | 'umland' | 'beide';
    active: boolean;
    config: WohnungCriteria;
    location: LocationRules;
  }>,
): Promise<{ profile: SearchProfile; rescored: number }> {
  return fetchJson(`/api/search-profiles/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

export async function deleteSearchProfile(id: number): Promise<{ ok: boolean; rescored: number }> {
  return fetchJson(`/api/search-profiles/${id}`, { method: 'DELETE' });
}

export async function getCriteria(
  profile = 'A_wohnung',
): Promise<{ profile: string; config: WohnungCriteria; defaults: WohnungCriteria }> {
  return fetchJson(`/api/settings/criteria?profile=${encodeURIComponent(profile)}`);
}

export async function putCriteria(
  profile: string,
  config: WohnungCriteria,
): Promise<{ ok: boolean; profile: string; config: WohnungCriteria; rescored: number }> {
  return fetchJson('/api/settings/criteria', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profile, config }),
  });
}

export async function previewGeocode(address: string): Promise<GeocodePreview> {
  return fetchJson('/api/anchors/geocode', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ address }),
  });
}

export async function createAnchor(input: AnchorInput): Promise<{ ok: boolean; anchor: UserAnchor }> {
  return fetchJson('/api/anchors', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function updateAnchor(
  id: number,
  input: AnchorInput,
): Promise<{ ok: boolean; anchor: UserAnchor }> {
  return fetchJson(`/api/anchors/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function deleteAnchor(id: number): Promise<{ ok: boolean }> {
  return fetchJson(`/api/anchors/${id}`, { method: 'DELETE' });
}

// ─── Postfach-Anbindung ─────────────────────────────────────────

export interface MailStatus {
  connected: boolean;
  imapHost: string | null;
  imapPort: number | null;
  mailUser: string | null;
  connectedAt: number | null;
  lastPollAt: number | null;
  lastError: string | null;
  isDefaultMailbox: boolean;
}

export interface MailCreds {
  imapHost: string;
  imapPort: number;
  mailUser: string;
  appPassword: string;
}

export async function getMailStatus(): Promise<MailStatus> {
  return fetchJson('/api/mail');
}

export async function testMail(creds: MailCreds): Promise<{ ok: boolean }> {
  return fetchJson('/api/mail/test', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(creds),
  });
}

export async function connectMail(creds: MailCreds): Promise<{ ok: boolean }> {
  return fetchJson('/api/mail', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(creds),
  });
}

export async function disconnectMail(): Promise<{ ok: boolean }> {
  return fetchJson('/api/mail', { method: 'DELETE' });
}

// ─── Benachrichtigungs-Kanäle (Push/Test) ───────────────────────

export interface NotificationStatus {
  webpushConfigured: boolean;
  webpushEnabled: boolean;
  webpushDevices: number;
  emailEnabled: boolean;
  ntfyTopic: string | null;
}

export async function getNotificationStatus(): Promise<NotificationStatus> {
  return fetchJson('/api/notification/status');
}

export async function getVapidKey(): Promise<{ publicKey: string | null }> {
  return fetchJson('/api/push/vapid');
}

export async function subscribePush(sub: PushSubscriptionJSON): Promise<{ ok: boolean }> {
  return fetchJson('/api/push/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(sub),
  });
}

export async function unsubscribePush(endpoint: string): Promise<{ ok: boolean }> {
  return fetchJson('/api/push/unsubscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  });
}

export async function testNotification(): Promise<{
  anyDelivered: boolean;
  perChannel: Array<{ kind: string; ok: boolean; error?: string }>;
}> {
  return fetchJson('/api/notification/test', { method: 'POST' });
}

// ─── Profile (Aktivieren/Pausieren) ───────────────────────────────

export interface ProfileInfo {
  name: string;
  displayName: string;
  active: boolean;
  pushThreshold: number;
}

export async function getProfiles(): Promise<{ profiles: ProfileInfo[] }> {
  return fetchJson('/api/profiles');
}

export async function setProfileActive(
  name: string,
  active: boolean,
): Promise<{ ok: boolean; name: string; active: boolean }> {
  return fetchJson(`/api/profiles/${encodeURIComponent(name)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ active }),
  });
}

// ─── Swipe API ────────────────────────────────────────────────

export type RejectCategory =
  | 'lage'
  | 'preis'
  | 'groesse'
  | 'zimmer'
  | 'zustand'
  | 'aussenbereich'
  | 'umgebung'
  | 'pendel'
  | 'vibe'
  | 'duplikat'
  | 'inseratweg'
  | 'sonstiges';

// Auf 5 gemergte Absagegründe reduziert (alte Werte bleiben im Backend-Enum gültig, damit
// historische Ablehnungen weiter lesbar sind). Die 5 decken Lage, Preis, Größe, Zustand + Rest.
export const REJECT_CATEGORIES: Array<{ value: RejectCategory; label: string; icon: string }> = [
  { value: 'lage', label: 'Lage & Umgebung', icon: '📍' },
  { value: 'preis', label: 'Preis zu hoch', icon: '💸' },
  { value: 'groesse', label: 'Größe / Zimmer', icon: '📐' },
  { value: 'zustand', label: 'Zustand / Ausstattung', icon: '🔧' },
  { value: 'sonstiges', label: 'Sonstiges', icon: '❓' },
];

export interface SwipeQueueResponse {
  queue: number[];
  totalCandidates?: number;
  filteredOut?: number;
}

export async function getSwipeQueue(
  searchProfileId?: number,
  minScore?: number,
  maxAgeDays?: number,
): Promise<SwipeQueueResponse> {
  const params = new URLSearchParams();
  if (searchProfileId) params.set('searchProfileId', String(searchProfileId));
  if (minScore !== undefined) params.set('minScore', String(minScore));
  if (maxAgeDays !== undefined) params.set('maxAgeDays', String(maxAgeDays));
  return fetchJson(`/api/swipe/queue?${params.toString()}`);
}

export async function rejectObject(
  id: number,
  category: RejectCategory,
  note?: string,
  by: 'user' | 'partner' = 'user',
): Promise<{ ok: boolean }> {
  return fetchJson(`/api/objects/${id}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ category, note, by }),
  });
}

/** „Partner ist auch dafür" → Hot-Vote der prüfenden Person; beide hot → Match. */
export async function partnerApproveObject(id: number): Promise<{ ok: boolean; matched?: boolean }> {
  return fetchJson(`/api/objects/${id}/partner-approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
}

export async function shortlistObject(
  id: number,
): Promise<{ ok: boolean; whatsappText: string; matched?: boolean }> {
  return fetchJson(`/api/objects/${id}/shortlist`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

/** Match → „In Kontakt": Makler/Verkäufer wurde kontaktiert (Terminal-Zustand). */
export async function markContacted(id: number): Promise<{ ok: boolean }> {
  return fetchJson(`/api/objects/${id}/contacted`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function unswipeObject(id: number): Promise<{ ok: boolean }> {
  return fetchJson(`/api/objects/${id}/unswipe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

export function scoreColor(s: number | null | undefined): string {
  if (s === null || s === undefined) return 'gray';
  if (s >= 0.75) return 'green';
  if (s >= 0.5) return 'yellow';
  if (s > 0) return 'orange';
  return 'red';
}

export interface PhaseInfo {
  /** 1..5 entlang der Pipeline; 0 = abgelehnt (raus). */
  num: number;
  /** Badge-Text. */
  label: string;
  /** Mantine-Farbe. */
  color: string;
}

/**
 * Leitet die Pipeline-Phase eines Objekts ab (ersetzt das alte „reif/roh"):
 *  1 Vorscore (ohne KI) · 2 KI-analysiert (unter Schwelle) · 3 zum Swipen (≥0.65) ·
 *  4 Partner-Check · 5 Kontaktiert · 0 Abgelehnt.
 */
export function objectPhase(obj: ObjectView): PhaseInfo {
  if (obj.status === 'rejected') return { num: 0, label: 'Abgelehnt', color: 'gray' };
  if (obj.status === 'contacted') return { num: 6, label: '✓ In Kontakt', color: 'teal' };
  if (obj.status === 'match') return { num: 5, label: '🎯 Match', color: 'yellow' };
  if (obj.status === 'partner_review')
    return { num: 4, label: 'Phase 4 · Partner', color: 'pink' };
  // status === 'new'
  const s = obj.score;
  if (!s || !s.isReif) return { num: 1, label: 'Phase 1 · Vorscore', color: 'gray' };
  const killed = (s.killedBy ?? null) !== null;
  // Phase-3-Gate = Swipe-Schwelle des Profils (p3Threshold, Default 0,65).
  const p3 = obj.p3Threshold ?? 0.65;
  if (killed || s.effectiveTotal < p3)
    return { num: 2, label: 'Phase 2 · KI-Analyse', color: 'yellow' };
  return { num: 3, label: 'Phase 3 · zum Swipen', color: 'violet' };
}

export function profileLabel(p: string | null): string {
  if (p === 'A_wohnung') return 'Miete';
  if (p === 'kauf') return 'Kauf';
  if (p === 'grundstueck') return 'Grundstück';
  if (p === 'B_buero') return 'Büro';
  return '—';
}

/**
 * Build a Google Maps search URL. Best-Effort:
 *  1. Wenn LLM eine Straße extrahiert hat + Adresse → genaue Suche
 *  2. Sonst lat/lng falls vorhanden
 *  3. Sonst die normalisierte Adresse
 */
export function buildMapsUrl(obj: ObjectView): string {
  const straße = obj.extracted?.['straße'];
  if (straße && straße !== 'null' && obj.address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${straße}, ${obj.address}`,
    )}`;
  }
  if (obj.lat !== null && obj.lng !== null) {
    return `https://www.google.com/maps/search/?api=1&query=${obj.lat},${obj.lng}`;
  }
  if (obj.address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(obj.address)}`;
  }
  return 'https://www.google.com/maps';
}

export function scoreColorClass(_s: number | null): string {
  // legacy alias for any caller — use scoreColor instead
  return '';
}
